/** @typedef {'quiz' | 'dnd' | 'ftb' | 'flashcard'} KnowledgeCheckType */

const SUPPORTED_TYPES = new Set(['quiz', 'dnd', 'ftb', 'flashcard'])

const KNOWLEDGE_CHECK_TYPE_LABELS = {
  quiz: 'Sub-quiz',
  dnd: 'Drag n Drop',
  ftb: 'Fill in the blank',
  flashcard: 'Flash cards',
}

const KNOWLEDGE_CHECK_TYPE_INSTRUCTIONS = {
  quiz: [
    'Select from the provided answer options for each respective question displayed',
    'To change your answer prior to submitting, select the alternative answer options to update your response',
  ],
  dnd: [
    'Select and drag answers from the answer bank to the connected terms space',
    'To change your answers prior to submitting, select and drag the answer to the desired connected term space',
  ],
  ftb: [
    'Select the blank space you\'re wanting to fill',
    'Select an answer from the answer bank to place within the space you\'ve selected to fill',
    'To change your answers prior to submitting, select the already filled space to clear the current answer and select the new answer from within the answer bank to apply',
  ],
}

const GROUP_WATCH_INSTRUCTOR_FIRST_STEP = 'Start Group Watch'

/** Human-readable label for a knowledge check activity type. */
export function knowledgeCheckTypeLabel (type) {
  if (type == null || type === '') {
    return ''
  }
  const key = String(type)
  return KNOWLEDGE_CHECK_TYPE_LABELS[key] ?? key
}

/**
 * How-to steps for a knowledge check type.
 * @param {string|null|undefined} type
 * @param {'course' | 'groupWatchLearner' | 'instructorGroupWatch'} [context='course']
 */
export function knowledgeCheckTypeInstructions (type, context = 'course') {
  if (type == null || type === '') {
    return null
  }
  const steps = KNOWLEDGE_CHECK_TYPE_INSTRUCTIONS[String(type)]
  if (!steps || !steps.length) {
    return null
  }
  if (context === 'instructorGroupWatch') {
    return [GROUP_WATCH_INSTRUCTOR_FIRST_STEP, ...steps]
  }
  return steps
}

export function isSupportedKnowledgeCheckType (type) {
  return typeof type === 'string' && SUPPORTED_TYPES.has(type)
}

/**
 * Knowledge checks included in course/module flows and instructor reports.
 * CMS/API uses `activityStatus` (or `activity_status`); only `'active'` is shown.
 */
export function isKnowledgeCheckActiveForCourseMaterial (kc) {
  if (!kc || typeof kc !== 'object') {
    return false
  }
  const raw = kc.activityStatus ?? kc.activity_status
  if (raw == null || String(raw).trim() === '') {
    return false
  }
  return String(raw).trim().toLowerCase() === 'active'
}

/**
 * Best stored attempt payload for re-showing a learner’s credited knowledge check
 * (selected answers + correct/incorrect). Backend may use any of these shapes.
 *
 * Prefer a frozen “completion” snapshot over a generic `attempt` so optional
 * practice retries can update `lastAttempt` without overwriting the display.
 *
 * @param {object|null|undefined} item One row from `user_course.knowledge_checks`
 * @returns {object|null} Shape mirrors KnowledgeCheckActivity `@submitted` payload
 */
export function completionAttemptFromProgressItem (item) {
  if (!item || typeof item !== 'object') {
    return null
  }
  const prefer =
    item.completionSnapshot ??
    item.completion_snapshot ??
    item.passingAttempt ??
    item.passing_attempt ??
    item.completionAttempt ??
    item.completion_attempt
  if (prefer && typeof prefer === 'object' && typeof prefer.type === 'string') {
    return prefer
  }
  const att = item.attempt ?? item.lastAttempt ?? item.last_attempt
  if (att && typeof att === 'object' && typeof att.type === 'string') {
    return att
  }
  return null
}

/**
 * @param {unknown} raw
 * @returns {object[]}
 */
export function normalizeKnowledgeChecks (raw) {
  if (!Array.isArray(raw)) {
    return []
  }
  return raw.map((item) => {
    if (!item || typeof item !== 'object') {
      return { __invalid: true }
    }
    return {
      ...item,
      active: item.active === false ? false : true,
      feedbackLog: Array.isArray(item.feedbackLog) ? item.feedbackLog : [],
      conceptLog: Array.isArray(item.conceptLog) ? item.conceptLog : []
    }
  })
}

/**
 * @param {string|number|null|undefined} a
 * @param {string|number} b
 */
export function knowledgeCheckVideoIdMatches (a, b) {
  if (a == null) {
    return false
  }
  return String(a) === String(b)
}

/**
 * @param {string|number|null|undefined} afterVideoId
 */
export function isAfterVideoIdBeforeFirst (afterVideoId) {
  return afterVideoId == null
}

/**
 * @param {object} module
 * @param {{ includeKnowledgeChecks?: boolean }} [options]
 * @returns {Array<{ kind: 'video'|'knowledgeCheck', video?: object, knowledgeCheckId?: string|number|null, knowledgeCheck?: object, unsupported?: boolean }>}
 */
export function buildModuleSidebarItems (module, options = {}) {
  if (!module || !Array.isArray(module.videos)) {
    return []
  }

  const includeKnowledgeChecks = options.includeKnowledgeChecks !== false
  const raw = includeKnowledgeChecks && Array.isArray(module.knowledge_checks)
    ? module.knowledge_checks
    : []
  const videos = module.videos

  const out = []
  const used = new Set()

  const pushKcFor = (afterVideoId) => {
    for (let i = 0; i < raw.length; i++) {
      if (used.has(i)) {
        continue
      }
      const kc = raw[i]
      if (!isKnowledgeCheckActiveForCourseMaterial(kc)) {
        continue
      }
      const isBefore = isAfterVideoIdBeforeFirst(kc.afterVideoId)
      const isMatch = !isBefore && afterVideoId != null && knowledgeCheckVideoIdMatches(kc.afterVideoId, afterVideoId)
      if ((afterVideoId == null && isBefore) || (afterVideoId != null && isMatch)) {
        used.add(i)
        if (!isSupportedKnowledgeCheckType(kc.type)) {
          console.warn(`[knowledge_checks] Unsupported type "${kc?.type}" at index ${i} — skipped in learner UI`)
        } else {
          out.push({
            kind: 'knowledgeCheck',
            knowledgeCheckId: kc.id != null ? kc.id : null,
            knowledgeCheck: kc,
            unsupported: false
          })
        }
      }
    }
  }

  // Before first video: null / undefined afterVideoId
  pushKcFor(null)

  for (const video of videos) {
    out.push({ kind: 'video', video })
    pushKcFor(video.id)
  }

  // Unmatched afterVideoId (e.g. deleted video) — after last video, stable raw order
  for (let i = 0; i < raw.length; i++) {
    if (used.has(i) || !isKnowledgeCheckActiveForCourseMaterial(raw[i])) {
      continue
    }
    const kc = raw[i]
    used.add(i)
    if (!isSupportedKnowledgeCheckType(kc.type)) {
      console.warn(`[knowledge_checks] Unsupported type "${kc?.type}" at index ${i} — skipped in learner UI`)
    } else {
      out.push({
        kind: 'knowledgeCheck',
        knowledgeCheckId: kc.id != null ? kc.id : null,
        knowledgeCheck: kc,
        unsupported: false
      })
    }
  }

  return out
}
